/**
 * features/finance/Remittance.tsx
 * ------------------------------------------------------------------
 * TELLING A SUPPLIER WHAT THE MONEY WAS FOR.
 *
 * A bulk payment lands in the supplier's bank as one lump with one
 * reference. On their side that is an unallocated receipt: they know
 * the school paid P84,300 and not which of their eleven outstanding
 * invoices it settles. So they allocate it to the oldest, or the wrong
 * one, and three weeks later they are chasing an invoice the school
 * paid and the school is proving it did.
 *
 * The advice is worth more to the school than to the supplier. It ends
 * that argument before it starts and costs nothing but sending.
 *
 * ONE ADVICE PER SUPPLIER. The run is how the school organises its own
 * work; a document listing ninety payments to other people would be a
 * data breach with a covering note.
 *
 * IT SHOWS THEIR REFERENCES, NOT OURS. "PV/2026/0184" means nothing to
 * somebody who has never seen the school's numbering. Their invoice
 * number and the order number are what they can match, so those lead
 * and the voucher number rides along as the school's own reference.
 *
 * THE ADVICES NOBODY CAN EMAIL ARE NAMED. A supplier with no address in
 * the register needs somebody to act, and a screen that silently sent
 * eight of nine would leave the ninth wondering.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  Mail, FileText, Download, MailWarning, AlertTriangle, Landmark,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { db } from '@/lib/firebase';
import {
  buildRemittances, remittableVouchers, advicesWithoutEmail, unmatchableLines,
  type RemittanceAdvice,
} from '@/lib/remittance';
import { VOUCHER_COLLECTIONS, type Voucher } from '@/lib/vouchers';
import { SUPPLIER_COLLECTION, type Supplier } from '@/lib/suppliers';
import {
  BATCH_COLLECTION, type PaymentBatch,
} from '@/lib/paymentBatches';
import { downloadTablePdf, type TableExport } from '@/lib/tableExport';
import { SCHOOL } from '@/lib/school.config';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Remittance() {
  const [vouchers, setVouchers] = useState<Voucher[] | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [batches, setBatches] = useState<PaymentBatch[]>([]);
  const [batchId, setBatchId] = useState<string>('');

  useEffect(() => onSnapshot(
    query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (s) => setVouchers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, SUPPLIER_COLLECTION)),
    (s) => setSuppliers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier)),
    () => setSuppliers([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, BATCH_COLLECTION)),
    (s) => setBatches(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PaymentBatch)),
    () => setBatches([]),
  ), []);

  /* Confirmed runs only. A run whose file is still with the bank has not
     paid anybody, and an advice for it would tell a supplier money is on
     the way that the school has not yet sent. */
  const sendable = useMemo(
    () => batches
      .filter((b) => b.status === 'confirmed')
      .sort((a, b) => (b.confirmedAt ?? '').localeCompare(a.confirmedAt ?? '')),
    [batches],
  );

  const batch = sendable.find((b) => b.id === batchId);

  const advices = useMemo(() => {
    if (!vouchers) return [];
    const scope = batch
      ? vouchers.filter((v) => batch.voucherIds?.includes(v.id))
      : [];
    return buildRemittances(remittableVouchers(scope), suppliers, {
      bankRef: batch?.bankRef,
      valueDate: batch?.valueDate,
    });
  }, [vouchers, suppliers, batch]);

  const noEmail = advicesWithoutEmail(advices);

  if (vouchers === null) return <div className="p-6 flex justify-center"><Spinner /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        icon={Mail} tone="navy"
        title="Remittance Advice"
        subtitle="What each supplier was paid, and against which of their invoices"
      />

      <SoftCard className="mb-5">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-gray-400">
            Which payment run
          </span>
          <select value={batchId} onChange={(e) => setBatchId(e.target.value)}
            className={glassFieldClass('mt-1 max-w-md')}>
            <option value="">— choose a confirmed run —</option>
            {sendable.map((b) => (
              <option key={b.id} value={b.id}>
                {b.batchNo} · {b.valueDate} · {money(b.total)} · {b.count} payments
              </option>
            ))}
          </select>
        </label>
        {sendable.length === 0 && (
          <p className="text-sm text-gray-500 mt-2">
            No confirmed runs yet. A run appears here once the bank has confirmed it —
            an advice for a run still in flight would tell suppliers money is on its way
            that the school has not sent.
          </p>
        )}
      </SoftCard>

      {batch && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <StatChip tone="navy" icon={Landmark} value={money(batch.total)} label="Paid in this run" />
            <StatChip tone="slate" value={String(advices.length)} label="Advices to send" />
            <StatChip
              tone={noEmail.length > 0 ? 'amber' : 'emerald'}
              icon={noEmail.length > 0 ? MailWarning : undefined}
              value={String(noEmail.length)} label="With no email on file" />
          </div>

          {noEmail.length > 0 && (
            <SoftCard className="mb-4 border-l-4 border-amber-400">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <MailWarning size={15} className="inline mr-1 -mt-0.5" />
                No email address on file for {noEmail.map((a) => a.supplierName).join(', ')}.
                Download those and send them another way, or add an address to the supplier
                register so the next run is automatic.
              </p>
            </SoftCard>
          )}

          <div className="space-y-4">
            {advices.map((a) => (
              <AdviceCard key={a.supplierId ?? a.supplierName} advice={a} batchNo={batch.batchNo} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AdviceCard({ advice, batchNo }: { advice: RemittanceAdvice; batchNo: string }) {
  const unmatchable = unmatchableLines(advice);

  function download() {
    const spec: TableExport<typeof advice.lines[number]> = {
      title: 'REMITTANCE ADVICE',
      meta: [
        advice.supplierName,
        advice.valueDate ? `Paid ${advice.valueDate}` : '',
        advice.bankRef ? `Bank reference ${advice.bankRef}` : '',
      ].filter(Boolean).join(' · '),
      columns: [
        { header: 'Your invoice', text: (l) => l.supplierRef ?? '—' },
        { header: 'Order', text: (l) => l.poNo ?? '—' },
        { header: 'Description', text: (l) => l.description },
        { header: 'Paid', text: (l) => l.paidAt },
        { header: 'Our ref', text: (l) => l.voucherNo },
        { header: 'Amount', text: (l) => money(l.amount), raw: (l) => l.amount, align: 'right' },
      ],
      rows: advice.lines,
      totals: ['', '', '', '', 'Total paid', money(advice.total)],
      note: `Paid by ${SCHOOL.name}. Please allocate against the invoices listed above. `
        + `Any query, quote ${batchNo}.`,
      filename: `remittance-${advice.supplierName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`,
    };
    downloadTablePdf(spec);
  }

  return (
    <SoftCard pad="p-0">
      <PanelHeader
        icon={FileText} tone="navy"
        title={advice.supplierName}
        subtitle={advice.email ?? 'No email on file — this one needs sending by hand'}
        right={(
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">
              {money(advice.total)}
            </span>
            <button type="button" className={btnGhost} onClick={download}>
              <Download size={14} /> PDF
            </button>
            {advice.email && (
              <a href={mailtoFor(advice, batchNo)} className={btnPrimary}>
                <Mail size={14} /> Email
              </a>
            )}
          </div>
        )}
      />

      {unmatchable.length > 0 && (
        <p className="px-4 pt-3 text-[12px] text-amber-700 dark:text-amber-300">
          <AlertTriangle size={12} className="inline -mt-0.5 mr-1" />
          {unmatchable.length} line{unmatchable.length === 1 ? '' : 's'} carry neither their
          invoice number nor an order number, so the supplier cannot match{' '}
          {unmatchable.length === 1 ? 'it' : 'them'} without ringing.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
              <th className="py-2 pl-4 pr-2 font-semibold">Their invoice</th>
              <th className="px-2 font-semibold">Order</th>
              <th className="px-2 font-semibold">Description</th>
              <th className="px-2 font-semibold">Paid</th>
              <th className="px-2 font-semibold">Our ref</th>
              <th className="px-2 pr-4 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {advice.lines.map((l) => (
              <tr key={l.voucherNo} className="border-b border-gray-50 dark:border-gray-800">
                <td className="py-2 pl-4 pr-2 font-medium text-gray-900 dark:text-white">
                  {l.supplierRef ?? <span className="text-gray-300 font-normal">not recorded</span>}
                </td>
                <td className="px-2 text-gray-500 text-xs">{l.poNo ?? '—'}</td>
                <td className="px-2 text-gray-700 dark:text-gray-200 truncate max-w-[240px]">
                  {l.description}
                </td>
                <td className="px-2 tabular-nums text-gray-500">{l.paidAt}</td>
                <td className="px-2 font-mono text-xs text-gray-400">{l.voucherNo}</td>
                <td className="px-2 pr-4 text-right tabular-nums">{money(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 dark:border-gray-700 font-bold">
              <td className="py-2 pl-4 pr-2" colSpan={5}>Total paid</td>
              <td className="px-2 pr-4 text-right tabular-nums">{money(advice.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </SoftCard>
  );
}

/**
 * A mailto rather than a Cloud Function, for now.
 *
 * Deliberate: the school's SMTP path is a deploy surface with a template
 * and a bounce story, and an advice that silently failed to send is worse
 * than none. This puts a correctly addressed, correctly worded message in
 * front of whoever is sending it, with the PDF to attach beside it. The
 * automatic version belongs here later, not as a half-built version now.
 */
function mailtoFor(advice: RemittanceAdvice, batchNo: string): string {
  const subject = `Remittance advice — ${SCHOOL.name}`;
  const body = [
    `Dear ${advice.supplierName},`,
    '',
    `We have paid ${money(advice.total)} into your account`
      + `${advice.valueDate ? ` with a value date of ${advice.valueDate}` : ''}`
      + `${advice.bankRef ? `, reference ${advice.bankRef}` : ''}.`,
    '',
    'It settles the following:',
    ...advice.lines.map((l) => `  ${l.supplierRef ?? l.voucherNo}  ${l.description}  ${money(l.amount)}`),
    '',
    `Total: ${money(advice.total)}`,
    '',
    `Please allocate accordingly. Any query, quote ${batchNo}.`,
    '',
    'Kind regards',
    SCHOOL.name,
  ].join('\n');

  return `mailto:${advice.email}`
    + `?subject=${encodeURIComponent(subject)}`
    + `&body=${encodeURIComponent(body)}`;
}
